// File: frontend/src/components/dashboard/RecentActivities.js

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, 
  LogIn, 
  LogOut, 
  UserCheck, 
  Phone, 
  Mail, 
  Shield, 
  Eye, 
  UserCog, 
  AlertTriangle,
  Bell,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import LoadingSpinner from '../utility/LoadingSpinner';
import styles from './RecentActivities.module.css';

/**
 * Event type display configuration
 */
const EVENT_CONFIG = {
  USER_SIGNED_IN: {
    label: 'Signed in',
    icon: LogIn,
    color: '#38C793'
  },
  USER_SIGN_IN_INITIATED: {
    label: 'Sign in initiated',
    icon: LogIn,
    color: '#6B7280'
  },
  USER_SIGNED_OUT: {
    label: 'Signed out',
    icon: LogOut,
    color: '#6B7280'
  },
  USER_ACCOUNT_ACTIVATED: {
    label: 'Account activated',
    icon: UserCheck,
    color: '#38C793'
  },
  USER_PROFILE_VIEWED: {
    label: 'Viewed profile',
    icon: Eye,
    color: '#2348ED'
  },
  USER_PROFILE_UPDATED: {
    label: 'Updated profile',
    icon: UserCog,
    color: '#8B5CF6'
  },
  USER_PHONE_VERIFIED: {
    label: 'Phone verified',
    icon: Phone,
    color: '#38C793'
  },
  USER_EMAIL_VERIFIED: {
    label: 'Email verified',
    icon: Mail,
    color: '#38C793'
  },
  USER_CONSENT_UPDATED: {
    label: 'Consent preferences updated',
    icon: Shield,
    color: '#6B7280'
  },
  USER_OTP_VERIFICATION_FAILED: {
    label: 'OTP verification failed',
    icon: AlertTriangle,
    color: '#EF4444'
  },
  NEWSLETTER_SUBSCRIBED: {
    label: 'Subscribed to newsletter',
    icon: Bell,
    color: '#2348ED'
  }
};

const DEFAULT_CONFIG = {
  label: null,
  icon: Activity,
  color: '#6B7280'
};

/**
 * Get display config for an event type
 */
const getEventConfig = (eventType) => {
  const config = EVENT_CONFIG[eventType];
  if (config) return config;

  return {
    ...DEFAULT_CONFIG,
    label: eventType
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^./, c => c.toUpperCase())
  };
};

/**
 * Format timestamp to relative time
 */
const formatRelativeTime = (isoString) => {
  if (!isoString) return '';

  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  }).format(date);
};

/**
 * Format timestamp to full date
 */
const formatFullDate = (isoString) => {
  if (!isoString) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(isoString));
};

const RecentActivities = ({ limit = 5, showViewAll = false }) => {
  const { user } = useAuth();

  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadActivities = useCallback(async () => {
    if (!user?.user_id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.getUserEvents(
        { limit },
        user.user_id
      );

      if (result.success) {
        setActivities(result.data.events || []);
      } else {
        setError(result.error || 'Failed to load activities');
      }
    } catch (err) {
      console.error('Error loading recent activities:', err);
      setError(err.message || 'Failed to load activities');
    } finally {
      setIsLoading(false);
    }
  }, [user?.user_id, limit]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.recentActivities}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
        </div>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="small" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.recentActivities}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
        </div>
        <div className={styles.errorContainer}>
          <AlertCircle size={20} />
          <p className={styles.errorText}>Unable to load recent activities</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <div className={styles.recentActivities}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
        </div>
        <div className={styles.emptyContainer}>
          <Activity size={32} className={styles.emptyIcon} />
          <p className={styles.emptyText}>No recent activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.recentActivities}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
          <p className={styles.sectionSubtitle}>Your latest account activities</p>
        </div>
        {showViewAll && activities.length > 0 && (
          <Link to="/account" className={styles.viewAllLink}>
            View all <ArrowRight size={14} />
          </Link>
        )}
      </div>
      <div className={styles.activitiesList}>
        {activities.map((event) => {
          const config = getEventConfig(event.event_type);
          const IconComponent = config.icon;

          return (
            <div key={event.event_id} className={styles.activityItem}>
              <div
                className={styles.activityIcon}
                style={{
                  background: `${config.color}15`,
                  color: config.color
                }}
              >
                <IconComponent size={16} strokeWidth={2} />
              </div>
              <div className={styles.activityContent}>
                <p className={styles.activityLabel}>{config.label}</p>
                <span 
                  className={styles.activityTime}
                  title={formatFullDate(event.event_timestamp)}
                >
                  {formatRelativeTime(event.event_timestamp)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivities;